import { useCallback, useState } from "react";
import { HiOutlineEye } from "react-icons/hi2";
import { Link } from "react-router-dom";
import { ActionIcon, Text, Tooltip } from "rizzui";
import { toast } from "sonner";
import DeletePopover from "../../../components/buttons/DeletePopover";
import { Loader } from "../../../components/loader/SpinnerLogo";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import CustomTable from "../../../components/table/CustomTable";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import useFetch from "../../../hooks/useFetch";
import useHandleErrors from "../../../hooks/useHandleErrors";
import Page from '../../../layout/Page/Page';
import { menuRoutes } from "../../../router/menu";
import CallFilters from "./CallFilters";
import {CallsService} from "../../../services/calls/callsService";
import {Call, CallsApiResponse} from "../../../type/entities/calls-type";

interface ListCallsProps {}

const columnOrder = [
    'id',
    'agent',
    'nif',
    'personInformation',
    'clientCompanyName',
    'clientId',
    'userId',
    'userName',
    'userLastName',
    'userEmail',
    'commercialNetworkName',
    'commercialNetworkCode',
    'commercialNetworkId',
    'departmentName',
    'departmentId',
    'status',
    'ocmEndText',
    'comment',
    'duration',
    'callDate'
];

const ListCalls: React.FC<ListCallsProps> = ({ }) => {

    // HOOKS

    const service = new CallsService();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [rowId, setRowId] = useState<string>('');
    const [selectedCall, setSelectedCall] = useState<Call | null>(null);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listCalls(filters);
        return response.getResponseData() as CallsApiResponse;
    }, [filters]));

    /**
     * Order the data to match the column order
     */
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });


    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR LLAMADA
     * @EN DELETE CALL
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteCall(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Llamada eliminada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR MÚLTIPLES LLAMADAS
     * @EN DELETE MULTIPLE CALLS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {
            const response = await (await service.deleteMultipleCalls(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Llamadas eliminadas correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES FORMATEA LA DURACIÓN DE UNA LLAMADA EN HUMAN READABLE
     * @EN FORMAT THE CALL DURATION IN HUMAN READABLE
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const formatDurationHuman = (seconds: number): string => {
        if (!seconds && seconds !== 0) return 'N/A';

        const totalSeconds = Math.round(seconds);

        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;

        let parts = [];
        if (h > 0) parts.push(`${h} h`);
        if (m > 0) parts.push(`${m} min`);
        if (s > 0 || parts.length === 0) parts.push(`${s} s`);

        return parts.join(' ');
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    return (
        <Page>
            {data?.data && (
                <CustomTable
                    largeTable
                    id={'calls-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    columnsNotShown={[
                        'clientId',
                        'userId',
                        'userName',
                        'userLastName',
                        'userEmail',
                        'clientCompanyName',
                        'commercialNetworkCode',
                        'productTypeRenewalSettings',
                        'commercialNetworkId',
                        'departmentId',
                        'nif'
                    ]}
                    overrideColumns={[
                        {
                            key: 'personInformation',
                            render: (row: any) => (
                                <div className="flex flex-col">
                                {row.row.original.clientCompanyName
                                    ? userCan('get_clients', 'clients')
                                        ? (
                                            <Link to={`${menuRoutes.clients.path}/${row.row.original.clientId}/profile${menuRoutes.clients.profile.info}`}>
                                                <span className="font-bold text-primary">{row.row.original.clientCompanyName}</span>
                                            </Link>
                                        )
                                        : <span className="font-bold text-primary">{row.row.original.clientCompanyName}</span>
                                    : 'N/A'
                                }
                                <div className="text-xs text-gray-500">{row.row.original.nif}</div>
                                </div>
                            )
                        },
                        {
                            key: 'agent',
                            render: (row: any) => (
                                <div className="flex flex-col">
                                    {userCan('get_user', 'user')
                                        ? (
                                            <Link to={`${menuRoutes.users.path}/${row.row.original.userId}/profile${menuRoutes.users.profile.info}`} className="font-bold text-primary">
                                                {row.row.original.userName} {row.row.original.userLastName || ''}
                                            </Link>
                                        )
                                        : <span className="font-medium">{row.row.original.userName} {row.row.original.userLastName || ''}</span>
                                    }
                                    <div className="text-xs text-gray-500">{row.row.original.userEmail}</div>
                                </div>
                            )
                        },
                        {
                            key: 'commercialNetworkName',
                            render: (row: any) => (
                                <div className="flex flex-col">
                                    <span className="font-medium">{row.row.original.commercialNetworkName}</span>
                                    <span className="text-xs text-gray-500">{row.row.original.commercialNetworkCode}</span>
                                </div>
                            )
                        },
                        {
                            key: 'ocmEndText',
                            render: (row: any) => (
                                <span className="font-medium">{row.row.original.ocmEndText || 'Sin datos'}</span>
                            )
                        },
                        {
                            key: 'duration',
                            render: (row: any) => (
                                <span className="font-medium">{formatDurationHuman(row.row.original.duration)}</span>
                            )
                        }
                    ]}
                    actions={[
                        {
                            label: 'View',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver llamada'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link to={`${menuRoutes.calls.path}/${row.id}/profile${menuRoutes.calls.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            }
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'calls', permission: 'delete_calls' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar llamada`}
                                                description={`¿Estás seguro de que deseas eliminar la llamada?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    multiDeletePermission={{ group: 'calls', action: 'delete_calls' }}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as CallsApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            {!data?.data && loading && (<Loader height='70vh' />)}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros de Llamadas'}>
                <CallFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
        </Page>
    );
};

export default ListCalls;
